import  { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, redirect, useLoaderData } from '@remix-run/react'
import { useState } from 'react'
import { useLocation } from "react-router-dom";
import { brandName } from '#app/components/branding.js'
import { Combobox } from '#app/components/ui/combobox'
import { usePersistedState } from '#app/hooks/use-persisted-state'
import { getBuildsByServer, getBuildsByTag } from '#app/utils/builds.server.js'
import { momentsAgo, prettyDateShort } from '#app/utils/dateFormat.js'
import { cleanSkillName } from '#app/utils/scripts.helper.js'
import { getServerList } from '#app/utils/selected-server-options.js'
import { getServerBySlug } from '#app/utils/shards.server.js'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    return [
    { title: `UO Builds by Tag | ${data?.tagName} | UO Addict` },
    { name: 'description', content: `Browse for Ultima Online builds under the tag ${data?.tagName} on ${brandName}` },
]}

export async function loader({ request, params }: LoaderFunctionArgs) {
    const {builds} = await getBuildsByTag({tagName: params.tagName ?? "Unknown"});
    const serverList = getServerList();

    return json({
        tagName: params.tagName ?? "Unknown",
        builds,
        serverList
    });
};

export default function IndexRoute() {
  const { builds, tagName, serverList } = useLoaderData<typeof loader>()
  const [selectedServer, setSelectedServer] = usePersistedState<string | null>('selectedServer', null)

  const filteredBuilds = selectedServer
    ? builds.filter(build => build.server.name === selectedServer)
    : builds

  return (
	<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
		<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">{filteredBuilds.length} UO Build Templates tagged as {tagName}</h1>
		</div>
		<p className="mt-2 text-center text-muted-foreground">
			Wether you are just getting started or are a seasoned veteran - these builds will help you get the most out of your UO experience.
		</p>
		<p className="mt-2 text-center">
			Have an idea for a build to share? <Link to={`/builds/new`} className="underline hover:no-underline">Add Your Build</Link>
		</p>
		<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
            <div className="w-full max-w-md mb-8">
                <Combobox
                    name="server"
                    value={selectedServer}
                    onChange={setSelectedServer}
                    options={serverList}
                    label="Filter by Server"
                    placeholder="Select a server"
                />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3">
        {
          filteredBuilds.map((build, index) => {

            let total = 0;
            const buildBreakout = build.build?.split(",").map(x => { 
              const skill = x.split("|")
              const skillName = skill[0] ?? "No skill name"
              const skillValue = Number(skill[1]) ?? 0
              total += skillValue
              return { name: skillName, value: skillValue }
            })

            return <div key={`build_${index}`}>
                <Link to={`/builds/${build.server.slug}/${build.slug}`}>
                {/* {build.description} 
                {build.difficulty} 
                {build.gear} 
                {build.taggables.map((taggable, index) => { return taggable.tag.name})}
                {build.characterId} */}
                {/* {build.name} {build.build} {build.buildSignature} */}

                  <div className="skill-scroll-container">
                        <div className='m-4'>
                            <div className='text-center m-auto min-h-32 align-text-bottom'>
                            <img src={build.server.logo} alt={build.server.name} className="max-h-24 server-logo m-auto" />
                            by {build.user.username} {momentsAgo(build.createdAt.toString())} ago
                            </div>
           
                            <div className="skill-container-top">
                                <div className='not-input'>{build.name.length ? build.name : "No build name"}</div>
                            </div>
                            <div className="skill-container skill-container-mid">
                                {buildBreakout?.sort( (a,b) => {
                                    if (a.name > b.name) {
                                        return 1;
                                    } else if (b.name > a.name) {
                                        return -1;
                                    }
                                    return 0;
                                }).map(x => { return <div className="skill-row" key={"selected_" + x.name}>
                                    <span className="name">{cleanSkillName(x.name)}</span> <span className="value">{x.value}</span>
                                    </div>})
                                }
                                <div className="skill-row totals">
                                    <span className="name">Total</span> <span className={total > 720 ? "value invalid" : "value"}>{total}</span>
                                </div>
                            </div>
                            <div className="skill-container-bot">&nbsp;</div>
                        </div>
                      </div>
                    </Link> 
                    <div>
                        {build.taggables.map((taggable, index) => { return <Link key={`tag_${taggable.tag.name}`} to={`/builds/tags/${taggable.tag.name}`} className="underline hover:no-underline m-2">{taggable.tag.name}</Link>})}
                    </div>
              </div>
          })
        }
			</div>
		</div>

	</div>
);
}

